import Layout from '../components/Layout';
import React, {Fragment, useContext, useEffect, useState, useMemo} from 'react';
import {useFlexLayout, useTable} from 'react-table';
import {StepStatus, useAppContext} from '../context/app-context';
import {useAuthContext, useClient} from 'app/gakki/context/auth-context';
import {faSpinner} from '@fortawesome/pro-light-svg-icons';
import {IconButton} from 'components/Button';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {
  QuestionContainer,
  QuestionTitle,
  QuestionBody,
} from '../components/Layout/question';
import {RadioButton} from 'components/form-items';
import Select from 'components/Select';
import Input from 'components/Input';
import {useForm, Controller} from 'react-hook-form';
import {DevTool} from '@hookform/devtools';
import {useMutation} from 'react-query';
import {cloneDeep} from 'lodash';
import {Table, Thead, Tr, Th, Td, Tbody, CenterCell} from 'components/table';
import {DateTime} from 'luxon';
import {formatYYYYMMDD} from 'app/gakki/utils/time';
import {ReactComponent as EditIcon} from 'app/gakki/assets/edit.svg';
import SalaryPeriodForm from 'app/gakki/components/salary-period-form';
import {useCalendarEvent} from 'app/gakki/api/payroll';

type SalaryPeriod = {
  year: number;
  month: number;
  salaryStart?: string;
  salaryEnd?: string;
  salaryDay?: string;
};

const dayOptions: {label: string; value: number}[] = [];
for (let i = 1; i <= 31; i++) {
  dayOptions.push({
    label: `${i}`,
    value: i,
  });
}

const monthOptions: {label: string; value: number}[] = [
  {label: '當月', value: 0},
  {label: '次月', value: 1},
];

const cycleMonthOptions: {label: string; value: number}[] = [
  {label: '上月', value: 1},
  {label: '當月', value: 2},
];

const defaultValues = {
  payrollSetting: {
    payrollDayOption: 0,
    payrollMonth: 0,
    payrollDay: 10,
  },
  // payrollOnVacationMethod: 1.提前發放 2.延後發放
  payrollOnVacationMethod: 1,
  payrollSalaryRange: {
    calculateCycleMonthStart: 2,
    calculateCycleDayStart: 1,
    calculateCycleMonthEnd: 2,
    calculateCycleDayEnd: 31,
  },
  payrollSalaryPeriodSettings: [],
};

function StepPYQuestions() {
  const api = useClient();
  const {companyId} = useAuthContext();
  const {
    changeStep,
    save,
    edit,
    updatedTime,
    stepStatus,
    data,
    init,
    setIsLoading,
    isLoading,
    step,
    saveAndGoStep,
    saveFail,
    confirm,
    refetchEnablePY,
    onPrev,
  } = useAppContext();

  const [showFailDialog, setShowFailDialog] = useState(false);
  const [shouldUpdateLocal, setShouldUpdateLocal] = useState(false);
  const [calculateEndDay, setCalculateEndDay] = useState('當月1日');
  const [calculateEndDayValue, setCalculateEndDayValue] = useState({
    date: 31,
    minusMonth: 1,
  });
  const [calendarEventYear, setCalendarEventYear] = useState<
    undefined | number
  >(undefined);
  const {data: calendarEventData, isLoading: isCalendarEventisLoading} =
    useCalendarEvent(calendarEventYear);
  const nationalHolidayList =
    calendarEventData
      ?.map((e: any) => !e.isMakeupDate && formatYYYYMMDD(e.eventDate))
      .filter(Boolean) || [];
  const makeupDayList =
    calendarEventData
      ?.map((e: any) => e.isMakeupDate && formatYYYYMMDD(e.eventDate))
      .filter(Boolean) || [];
  const isWeekend = (date: DateTime) => /6|7/i.test(String(date.weekday));
  const isNationalHoliday = (dateString: string) =>
    nationalHolidayList.some((d: string) => d === dateString);
  const isMakeupDay = (dateString: string) =>
    makeupDayList.some((d: string) => d === dateString);

  const saveData = () => {
    const submitData: any = cloneDeep(data);
    if (submitData?.payrollSetting?.payrollDayOption === 0) {
      submitData.payrollSetting.payrollMonth = 1;
      submitData.payrollSetting.payrollDay = 5;
    }
    if (submitData?.payrollSetting?.payrollDayOption === 1) {
      submitData.payrollSetting.payrollMonth = 1;
      submitData.payrollSetting.payrollDay = 10;
    }
    if (submitData?.payrollSetting?.payrollDayOption === 2) {
      submitData.payrollSetting.payrollMonth = 0;
      submitData.payrollSetting.payrollDay = 31;
    }
    return api.put(`payroll/company/${companyId}/introduction`, {
      data: {...submitData, payrollSalaryPeriodSettings},
    });
  };

  const confirmData = () => {
    return api.put(`payroll/company/${companyId}/introduction/confirm`);
  };
  const {mutate: saveMutate} = useMutation(saveData, {
    onSuccess: () => save(),
    onError: () => saveFail(),
    onMutate: () => setIsLoading(true),
    onSettled: () => setIsLoading(false),
  });

  const {mutate: handleConfirm} = useMutation(
    () => saveData()
    .then(() => refetchEnablePY('next', step + 1))
    .then(async (company) => {
      try {
        if (company.enablePY) {
          await confirmData();
          changeStep(step + 1);
          setIsLoading(false)
        } else {
          changeStep(11);
        }
        confirm();
      } catch (e: any) {
        throw new Error(e);
      }
    }),
    {
      onError: () => {
        setIsLoading(false)
        setShowFailDialog(true)
      },
      onMutate: () => setIsLoading(true),
    },
  );
  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState,
    errors,
    getValues,
    setValue,
  } = useForm();
  const initSalaryPeriod = {
    year: new Date().getFullYear(),
    month: 1,
    salaryStart: undefined,
    salaryEnd: undefined,
    salaryDay: undefined,
  };
  const [payrollSalaryPeriodSettings, setPayrollSalaryPeriodSettings] =
    useState<SalaryPeriod[]>(defaultValues.payrollSalaryPeriodSettings);
  const [editingSalaryPeriod, setEditingSalaryPeriod] =
    useState<SalaryPeriod>(initSalaryPeriod);
  const payrollDayOptionValue = watch('payrollSetting.payrollDayOption');
  const isOtherPayDay = payrollDayOptionValue === 3;

  useEffect(() => {
    setIsLoading(true);
    if (data !== null && stepStatus !== StepStatus.init) {
      setIsLoading(false);
      reset(data);
      setCalendarEventYear(
        (data as any).payrollSalaryPeriodSettings?.[0]?.year ||
          new Date().getFullYear(),
      );
      showEndDay(data);
    }
    api
      .get(`payroll/company/${companyId}/introduction`)
      .then(({data}) => {
        setCalendarEventYear(
          data?.payrollSalaryPeriodSettings?.[0]?.year ||
            new Date().getFullYear(),
        );
        if (!data || data?.payrollOnVacationMethod === null) {
          init(defaultValues);
          reset(defaultValues);
          showEndDay(defaultValues);
        } else {
          init(data);
          reset(data);
          showEndDay(data);
        }
      })
      .finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (data && calendarEventYear && !isCalendarEventisLoading) {
      setPayrollSalaryPeriodSettings(
        generateSalaryPeriod(
          (data as any).payrollSalaryPeriodSettings || [],
          (data as any).payrollSetting,
        ),
      );
    }
  }, [data && calendarEventYear, isCalendarEventisLoading]);

  useEffect(() => {
    if (shouldUpdateLocal) {
      updateLocalData();
      setShouldUpdateLocal(false);
    }
  }, [shouldUpdateLocal]);

  useEffect(() => {
    updateSalaryPeriod();
  }, [calculateEndDayValue]);

  useEffect(() => {
    if (typeof saveAndGoStep === 'number') {
      saveMutate()
    }
  }, [saveAndGoStep])

  const handleNext = () => {
    handleConfirm();
  };

  const updateLocalData = () => {
    const formValues = watch();
    edit({
      ...formValues,
      payrollSalaryPeriodSettings,
    });
  };

  const showEndDay = (initData: any = {}) => {
    const _data: any = cloneDeep(initData);
    const calculateCycleMonthStart =
      getValues('payrollSalaryRange.calculateCycleMonthStart') |
      _data?.payrollSalaryRange?.calculateCycleMonthStart;
    const calculateCycleDayStart =
      getValues('payrollSalaryRange.calculateCycleDayStart') |
      _data?.payrollSalaryRange?.calculateCycleDayStart;

    let showText = '';
    if (calculateCycleDayStart === 1) {
      setValue(
        'payrollSalaryRange.calculateCycleMonthEnd',
        calculateCycleMonthStart,
      );
      setValue('payrollSalaryRange.calculateCycleDayEnd', 31);
      showText = `${calculateCycleMonthStart === 1 ? '上月' : '當月'}${31}日`;
      setCalculateEndDayValue({
        date: 31,
        minusMonth: calculateCycleMonthStart === 1 ? 1 : 0,
      });
    } else {
      setValue(
        'payrollSalaryRange.calculateCycleMonthEnd',
        calculateCycleMonthStart + 1,
      );
      setValue(
        'payrollSalaryRange.calculateCycleDayEnd',
        calculateCycleDayStart - 1,
      );
      let str = '';
      switch (calculateCycleMonthStart + 1) {
        case 2:
          str = '當月';
          setCalculateEndDayValue({
            date: calculateCycleDayStart - 1,
            minusMonth: 0,
          });
          break;
        case 3:
          str = '次月';
          setCalculateEndDayValue({
            date: calculateCycleDayStart - 1,
            minusMonth: -1,
          });
          break;
        default:
          break;
      }

      showText = `${str}${calculateCycleDayStart - 1}日`;
    }

    setCalculateEndDay(showText);
  };

  const getSalaryStart = (year: number, month: number) => {
    const calculateCycleMonthStart = getValues(
      'payrollSalaryRange.calculateCycleMonthStart',
    );
    const calculateCycleDayStart = getValues(
      'payrollSalaryRange.calculateCycleDayStart',
    );
    const isPreMonth = 1 === calculateCycleMonthStart;
    const isCurrentMonth = 2 === calculateCycleMonthStart;
    let salaryStart = DateTime.local(year, month, calculateCycleDayStart).minus(
      {months: isPreMonth ? 1 : 0},
    );
    if (!salaryStart.isValid && isPreMonth) {
      salaryStart = DateTime.local(year, month - 1, calculateCycleDayStart);
    }
    if (!salaryStart.isValid && isCurrentMonth) {
      salaryStart = DateTime.local(year, month).endOf('month');
    }
    return salaryStart.toISODate();
  };

  const getSalaryEnd = (year: number, month: number) => {
    const salaryEnd = DateTime.local(year, month).minus({
      months: calculateEndDayValue.minusMonth,
    });
    let date = getValues('payrollSalaryRange.calculateCycleDayEnd');

    const lastDateOfMonth = salaryEnd.endOf('month').day;
    date = date > lastDateOfMonth ? lastDateOfMonth : date;

    return salaryEnd.set({day: date}).toISODate();
  };

  const checkDateIsHoliday = (date: string) => {
    let tempDate = date;
    const payrollOnVacationMethod = getValues('payrollOnVacationMethod') || 1;
    const isEarlyPay = payrollOnVacationMethod === 1;

    while (
      isWeekend(DateTime.fromJSDate(new Date(tempDate))) ||
      isNationalHoliday(tempDate)
    ) {
      if (isMakeupDay(tempDate)) {
        return tempDate;
      }
      if (isEarlyPay) {
        tempDate = formatYYYYMMDD(
          DateTime.fromJSDate(new Date(tempDate)).minus({days: 1}).toISODate(),
        );
      } else {
        tempDate = formatYYYYMMDD(
          DateTime.fromJSDate(new Date(tempDate)).plus({days: 1}).toISODate(),
        );
      }
    }
    return tempDate;
  };

  const getSalaryDay = (
    year: number,
    month: number,
    payrollSetting: {
      payrollDayOption: number;
      payrollMonth: number;
      payrollDay: number;
    },
  ) => {
    let result = DateTime.local(year, month, 5).plus({month: 1});
    switch (payrollSetting.payrollDayOption) {
      case 0: // 次月5日
        result = DateTime.local(year, month, 5).plus({month: 1});
        break;
      case 1: // 次月10日
        result = DateTime.local(year, month, 10).plus({month: 1});
        break;
      case 2: // 當月最後一個工作日
        result = DateTime.local(year, month).endOf('month');
        break;
      case 3: // 其他選項
        const isNextMonth = payrollSetting.payrollMonth === 1;
        const isCurrentMonth = payrollSetting.payrollMonth === 0;
        let customPayDay = DateTime.local(
          year,
          month,
          payrollSetting.payrollDay,
        ).plus({month: isNextMonth ? 1 : 0});
        if (!customPayDay.isValid && isNextMonth) {
          customPayDay = DateTime.local(
            year,
            month + 1,
            payrollSetting.payrollDay,
          );
        }
        if (!customPayDay.isValid && isCurrentMonth) {
          customPayDay = DateTime.local(year, month).endOf('month');
        }
        result = customPayDay;
        break;
      default:
        result = DateTime.local(year, month, 5).plus({month: 1});
        break;
    }

    return checkDateIsHoliday(formatYYYYMMDD(result.toISODate()));
  };

  const generateSalaryPeriod = (
    defaultPeriod?: {year: number; month: number; salaryDay: string | null}[],
    currentPayrollSetting?: {
      payrollDayOption?: number;
      payrollMonth?: number;
      payrollDay?: number;
    },
  ) => {
    const year = calendarEventYear || new Date().getFullYear();
    const payrollDayOption =
      currentPayrollSetting?.payrollDayOption ||
      getValues('payrollSetting.payrollDayOption') ||
      defaultValues.payrollSetting.payrollDayOption;
    const payrollMonth =
      currentPayrollSetting?.payrollMonth ||
      getValues('payrollSetting.payrollMonth') ||
      defaultValues.payrollSetting.payrollMonth;
    const payrollDay =
      currentPayrollSetting?.payrollDay ||
      getValues('payrollSetting.payrollDay') ||
      defaultValues.payrollSetting.payrollDay;
    const _payrollSetting = {
      payrollDayOption,
      payrollMonth,
      payrollDay,
    };
    return new Array(12).fill(0).map((_, idx) => {
      const month = idx + 1;
      return {
        year,
        month,
        salaryStart: formatYYYYMMDD(getSalaryStart(year, month)),
        salaryEnd: formatYYYYMMDD(getSalaryEnd(year, month)),
        salaryDay: defaultPeriod?.[idx]?.salaryDay
          ? formatYYYYMMDD(defaultPeriod?.[idx]?.salaryDay as string)
          : getSalaryDay(year, month, _payrollSetting),
      };
    });
  };

  const updateSalaryPeriod = () => {
    setPayrollSalaryPeriodSettings(
      payrollSalaryPeriodSettings.map(period => {
        return {
          ...period,
          salaryStart: formatYYYYMMDD(
            getSalaryStart(period.year, period.month),
          ),
          salaryEnd: formatYYYYMMDD(getSalaryEnd(period.year, period.month)),
        };
      }),
    );
  };

  const updateSalaryDay = (data: any) => {
    const year = Number(data.year);
    const month = Number(data.month);
    setPayrollSalaryPeriodSettings(
      payrollSalaryPeriodSettings.map(period => {
        if (period.year === year && period.month === month) {
          return {...period, salaryDay: formatYYYYMMDD(data.salaryDay)};
        }
        return period;
      }),
    );
    closeSalaryPeriodForm();
  };

  const closeSalaryPeriodForm = () => setEditingSalaryPeriod(initSalaryPeriod);
  const columns = useMemo(() => {
    let columns: any[] = [
      {
        Header: '年度',
        accessor: 'year',
        width: 50,
        minWidth: 50,
        maxWidth: 50,
      },
      {
        Header: '月份',
        accessor: 'month',
        width: 50,
        minWidth: 50,
        maxWidth: 50,
      },
      {
        Header: '計薪週期(起)',
        accessor: 'salaryStart',
        width: 50,
        minWidth: 50,
        maxWidth: 50,
      },
      {
        Header: '計薪週期(迄)',
        accessor: 'salaryEnd',
        width: 50,
        minWidth: 50,
        maxWidth: 50,
      },
      {
        Header: '發薪日',
        accessor: 'salaryDay',
        width: 50,
        minWidth: 50,
        maxWidth: 50,
      },
      {
        Header: '編輯',
        accessor: 'edit',
        width: 30,
        minWidth: 30,
        maxWidth: 30,
        //@ts-ignore
        Cell: props => {
          return (
            <IconButton
              className={'edit-btn'}
              onClick={() => {
                setEditingSalaryPeriod(props.row.values);
              }}
            >
              <EditIcon className={'text-xl'} />
            </IconButton>
          );
        },
      },
    ];
    return columns;
  }, [payrollSalaryPeriodSettings]);

  const {rows, prepareRow, getTableBodyProps, getTableProps, headerGroups} =
    useTable<any>(
      {
        columns,
        data: payrollSalaryPeriodSettings,
        getRowId: row => row.month,
      },
      useFlexLayout,
    );

  return (
    <Fragment>
      <QuestionContainer title={'發薪設定'}>
        <>
          <QuestionBody>
            <QuestionTitle>Q1：請問貴公司是每月幾號發薪 ?</QuestionTitle>
            <Controller
              name={'payrollSetting.payrollDayOption'}
              control={control}
              render={props => {
                return (
                  <div
                    className={'grid grid-cols-3 gap-[15px]'}
                    data-test-id={'payroll-day-option'}
                  >
                    <RadioButton
                      name={props.name}
                      labelName={'次月5日'}
                      checked={props.value === 0}
                      onChange={() => {
                        props.onChange(0);
                        setPayrollSalaryPeriodSettings(
                          generateSalaryPeriod(undefined, {
                            payrollDayOption: 0,
                          }),
                        );
                        setShouldUpdateLocal(true);
                      }}
                    />
                    <RadioButton
                      name={props.name}
                      labelName={'次月10日'}
                      checked={props.value === 1}
                      onChange={() => {
                        props.onChange(1);
                        setPayrollSalaryPeriodSettings(
                          generateSalaryPeriod(undefined, {
                            payrollDayOption: 1,
                          }),
                        );
                        setShouldUpdateLocal(true);
                      }}
                    />
                    <RadioButton
                      name={props.name}
                      labelName={'當月最後一個工作日'}
                      checked={props.value === 2}
                      onChange={() => {
                        props.onChange(2);
                        setPayrollSalaryPeriodSettings(
                          generateSalaryPeriod(undefined, {
                            payrollDayOption: 2,
                          }),
                        );
                        setShouldUpdateLocal(true);
                      }}
                    />
                    <RadioButton
                      name={props.name}
                      labelName={'其他選項'}
                      checked={props.value === 3}
                      onChange={() => {
                        props.onChange(3);
                        setPayrollSalaryPeriodSettings(
                          generateSalaryPeriod(undefined, {
                            payrollDayOption: 3,
                          }),
                        );
                        setShouldUpdateLocal(true);
                      }}
                    />
                  </div>
                );
              }}
            />
            <div
              className={
                'mt-[34.5px] grid gap-x-[15px] grid-cols-[auto,80px,80px,auto,1fr] items-center'
              }
            >
              請輸入發薪日
              <Controller
                name={'payrollSetting.payrollMonth'}
                control={control}
                render={({onChange, value}) => {
                  return (
                    <div data-test-id={'payroll-month'}>
                      <Select
                        value={monthOptions.find(o => o.value === value)}
                        onChange={(o: any) => {
                          onChange(o.value);
                          setPayrollSalaryPeriodSettings(
                            generateSalaryPeriod(undefined, {
                              payrollMonth: o.value,
                            }),
                          );
                          setShouldUpdateLocal(true);
                        }}
                        options={monthOptions}
                        maxMenuHeight={100}
                        error={
                          formState.isSubmitted &&
                          errors?.payrollSetting?.payrollMonth
                        }
                        isDisabled={!isOtherPayDay}
                      />
                    </div>
                  );
                }}
              />
              <Controller
                name={'payrollSetting.payrollDay'}
                control={control}
                render={({onChange, value}) => {
                  return (
                    <div data-test-id={'payroll-day'}>
                      <Select
                        value={dayOptions.find(o => o.value === value)}
                        onChange={(o: any) => {
                          onChange(o.value);
                          setPayrollSalaryPeriodSettings(
                            generateSalaryPeriod(undefined, {
                              payrollDay: o.value,
                            }),
                          );
                          setShouldUpdateLocal(true);
                        }}
                        options={dayOptions}
                        maxMenuHeight={200}
                        error={
                          formState.isSubmitted &&
                          errors?.payrollSetting?.payrollDay
                        }
                        isDisabled={!isOtherPayDay}
                      />
                    </div>
                  );
                }}
              />
              日
            </div>
          </QuestionBody>
          <QuestionBody>
            <QuestionTitle>Q2：薪資計算範圍是 ?</QuestionTitle>
            <div
              className={
                'grid gap-x-[15px] grid-cols-[auto,80px,80px,50px,1fr] items-center mt-[34.5px]'
              }
            >
              請輸入計薪開始日
              <Controller
                name={'payrollSalaryRange.calculateCycleMonthStart'}
                control={control}
                render={({onChange, value}) => {
                  return (
                    <div data-test-id={'payroll-calculate-cycle-month'}>
                      <Select
                        value={cycleMonthOptions.find(o => o.value === value)}
                        onChange={(o: any) => {
                          onChange(o.value);
                          setShouldUpdateLocal(true);
                          showEndDay();
                        }}
                        options={cycleMonthOptions}
                        maxMenuHeight={100}
                        error={
                          formState.isSubmitted &&
                          errors?.payrollSalaryRange?.calculateCycleMonthStart
                        }
                      />
                    </div>
                  );
                }}
              />
              <Controller
                name={'payrollSalaryRange.calculateCycleDayStart'}
                control={control}
                render={({onChange, value}) => {
                  return (
                    <div data-test-id={'payroll-calculate-cycle-day'}>
                      <Select
                        value={dayOptions.find(o => o.value === value)}
                        onChange={(o: any) => {
                          onChange(o.value);
                          setShouldUpdateLocal(true);
                          showEndDay();
                        }}
                        options={dayOptions}
                        maxMenuHeight={200}
                        error={
                          formState.isSubmitted &&
                          errors?.payrollSalaryRange?.calculateCycleDayStart
                        }
                      />
                    </div>
                  );
                }}
              />
              日<span>計薪結束日&nbsp;&nbsp;{calculateEndDay}</span>
              <Controller
                as={<Input placeholder="calculateCycleMonthEnd" hidden />}
                name="payrollSalaryRange.calculateCycleMonthEnd"
                control={control}
                defaultValue={1}
              />
              <Controller
                as={<Input placeholder="calculateCycleDayEnd" hidden />}
                name="payrollSalaryRange.calculateCycleDayEnd"
                control={control}
                defaultValue={5}
              />
            </div>
          </QuestionBody>
          <QuestionBody>
            <QuestionTitle>Q3：發薪日，若遇到假日則 ?</QuestionTitle>
            <Controller
              name={'payrollOnVacationMethod'}
              control={control}
              render={props => {
                return (
                  <div
                    className={'grid gap-x-[15px] grid-cols-3'}
                    data-test-id={'payroll-on-vacation-method'}
                  >
                    <RadioButton
                      name={props.name}
                      labelName={'提前發放'}
                      checked={props.value === 1}
                      onChange={() => {
                        props.onChange(1);
                        setPayrollSalaryPeriodSettings(generateSalaryPeriod());
                        setShouldUpdateLocal(true);
                      }}
                    />
                    <RadioButton
                      name={props.name}
                      labelName={'延後發放'}
                      checked={props.value === 2}
                      onChange={() => {
                        props.onChange(2);
                        setPayrollSalaryPeriodSettings(generateSalaryPeriod());
                        setShouldUpdateLocal(true);
                      }}
                    />
                  </div>
                );
              }}
            />
          </QuestionBody>
          <QuestionBody>
            <QuestionTitle>Q4：需要調整一下該年度的發薪日嗎？</QuestionTitle>
            <Table {...getTableProps()} className={'border border-[#d5dadd]'}>
              <Thead>
                {headerGroups.map(headerGroup => (
                  <Tr {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map(column => (
                      <Th {...column.getHeaderProps()}>
                        <CenterCell className={'!text-left'}>
                          {column.render('Header')}
                        </CenterCell>
                      </Th>
                    ))}
                  </Tr>
                ))}
              </Thead>
              <Tbody {...getTableBodyProps()}>
                {isCalendarEventisLoading && (
                  <div className={'text-center p-[10px]'}>
                    <FontAwesomeIcon icon={faSpinner} pulse size={'2x'} />
                  </div>
                )}
                {!isCalendarEventisLoading &&
                  rows.map(row => {
                    prepareRow(row);
                    return (
                      <Tr {...row.getRowProps()} className={'text-left'}>
                        {row.cells.map(cell => {
                          return (
                            <Td {...cell.getCellProps()}>
                              {cell.render('Cell')}
                            </Td>
                          );
                        })}
                      </Tr>
                    );
                  })}
              </Tbody>
            </Table>
          </QuestionBody>
        </>
      </QuestionContainer>
      <Layout.Footer
        showNextButton
        showPrevButton
        showSaveButton
        onPrev={onPrev}
        onNext={handleSubmit(() => handleNext())}
        onSave={saveMutate}
        updatedTime={updatedTime}
        disabledSaveButton={stepStatus !== StepStatus.edit || isLoading || (!calendarEventYear || isCalendarEventisLoading)}
        disabledPrevButton={stepStatus === StepStatus.edit || isLoading || (!calendarEventYear || isCalendarEventisLoading)}
        disabledNextButton={isLoading || (!calendarEventYear || isCalendarEventisLoading)}
      />
      {editingSalaryPeriod.salaryDay && (
        <SalaryPeriodForm
          defaultValues={editingSalaryPeriod}
          onClose={closeSalaryPeriodForm}
          onSubmit={updateSalaryDay}
        />
      )}
      <DevTool control={control} />
    </Fragment>
  );
}
export default StepPYQuestions;
